import { useMemo } from 'react';

import dayjs from 'dayjs';
import { CreateAppointmentRequest, EventResponse } from 'generated';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useRecoilState } from 'recoil';

import { mutations } from 'RestAPIs/rapini';

import { appointmentToCopyAtom } from '../../capacity/Formation/atoms';

import { showSnackbar } from 'components/shared';

interface PasteAppointment {
  startDate: string;
  organizationalUnitId?: number;
  workPlaceEntityId?: string;
  activityEntityId?: string;
  projectEntityId?: string;
}

export type CopyModeReturn = {
  isCopyMode: boolean;
  copyId?: string;
  startCopyMode: (appointment: EventResponse) => void;
  endCopyMode: () => void;
  pasteAppointment: (appointment: PasteAppointment) => void;
  isLoading: boolean;
};

const useCopyMode = (): CopyModeReturn => {
  const { t } = useTranslation();
  const [appointmentToCopy, setAppointmentToCopy] = useRecoilState(
    appointmentToCopyAtom
  );
  const isCopyMode = useMemo(() => !!appointmentToCopy, [appointmentToCopy]);
  const copyId = useMemo(() => appointmentToCopy?.id, [appointmentToCopy]);
  const queryClient = useQueryClient();
  const { mutate: createAppointment, isLoading } =
    mutations.useCreateAppointment();

  const startCopyMode = (appointment: EventResponse) => {
    setAppointmentToCopy(appointment);
  };
  const endCopyMode = () => {
    setAppointmentToCopy(null);
  };

  const pasteAppointment = ({
    startDate,
    organizationalUnitId,
    workPlaceEntityId,
    projectEntityId
  }: PasteAppointment) => {
    if (!appointmentToCopy) {
      return;
    }
    const start = dayjs(startDate)
      .set('hour', dayjs(appointmentToCopy.start).hour())
      .set('minute', dayjs(appointmentToCopy.start).minute())
      .toISOString();

    const employee = appointmentToCopy.employees?.[0];

    const payload: CreateAppointmentRequest = {
      organizationalUnitId: organizationalUnitId,
      start,
      durationInSeconds: appointmentToCopy.durationInSeconds,
      workPlaceEntityId: workPlaceEntityId,
      activityEntityId: appointmentToCopy.activity?.entityId,
      projectEntityId: projectEntityId,
      employeeEntityIds:
        appointmentToCopy.project && employee?.entityId
          ? [employee.entityId]
          : undefined,
      notePublic: '',
      noteInternal: ''
    };
    createAppointment(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries('planning');
        showSnackbar({ severity: 'success', message: t('changesSaved') });
      }
    });
  };

  return {
    isCopyMode,
    copyId,
    startCopyMode,
    endCopyMode,
    pasteAppointment,
    isLoading
  };
};

export default useCopyMode;
